import { useState, useRef, useEffect } from 'react'
import { IconRobot, IconRefresh, IconSend } from '@/components/icons/Icons'

/**
 * El chat de prueba del recepcionista.
 *
 * Corre el MISMO motor que atiende a un cliente real, pero sin persistir nada y sin
 * enviar: el estado del turno vive acá y se devuelve en cada llamada. Hasta hoy, probar
 * el bot significaba meterle filas a la base por SSH y mandarle WhatsApps de verdad a
 * alguien — eso no lo puede hacer quien edita los textos.
 *
 * Las **notas del sistema** son lo que lo vuelve una herramienta y no una demo: dicen lo
 * que el flujo haría y acá no hace, como agendar.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface Linea {
  de: 'bot' | 'yo' | 'sistema'
  texto: string
}

interface TurnoApi {
  mensajes: string[]
  opciones: string[]
  estado: string
  nodo: string | null
  datos: Record<string, string>
}

interface Props {
  slug: string
  /** Probar el borrador en vez de lo publicado. */
  borrador: boolean
  /** Avisa si hay cambios sin guardar: la prueba corre lo GUARDADO, no lo que está en pantalla. */
  haySinGuardar: boolean
  /**
   * El primer mensaje ES el reporte, y se lee antes de arrancar.
   *
   * Lo pone el bot de soporte, que se suma a una charla en curso. El recepcionista no:
   * él abre la conversación y su primer mensaje es el saludo.
   */
  leerTodo?: boolean
}

export default function ChatDePrueba({ slug, borrador, haySinGuardar, leerTodo }: Props) {
  const [lineas, setLineas] = useState<Linea[]>([])
  const [turno, setTurno] = useState<TurnoApi | null>(null)
  const [slots, setSlots] = useState<any[]>([])
  const [entrada, setEntrada] = useState('')
  const [cargando, setCargando] = useState(false)
  const fin = useRef<HTMLDivElement>(null)

  useEffect(() => { fin.current?.scrollIntoView({ behavior: 'smooth' }) }, [lineas])
  // Cambiar de agente o de versión invalida el hilo: lo anterior no significa nada.
  useEffect(() => { reiniciar() }, [slug, borrador])

  const reiniciar = () => {
    setLineas([])
    setTurno(null)
    setSlots([])
  }

  const llamar = async (cuerpo: Record<string, unknown>) => {
    const token = localStorage.getItem('auth_token')
    const res = await fetch(`${API_URL}/api/agentes/${slug}/simular`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...cuerpo, borrador }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error ?? 'No se pudo simular')
    }
    return res.json()
  }

  const pintar = (d: any, opts: { silenciarMensajes?: boolean } = {}) => {
    const nuevas: Linea[] = opts.silenciarMensajes
      ? []
      : (d.turno.mensajes ?? []).map((m: string) => ({ de: 'bot' as const, texto: m }))
    for (const n of d.notas ?? []) nuevas.push({ de: 'sistema', texto: n })

    if (d.turno.estado === 'fin') nuevas.push({ de: 'sistema', texto: 'La conversación terminó acá.' })
    if (d.turno.estado === 'handoff') nuevas.push({ de: 'sistema', texto: 'Acá el bot deja de responder y queda para una persona.' })

    setLineas(l => [...l, ...nuevas])
    setTurno(d.turno)
    if (d.slots?.length) setSlots(d.slots)
  }

  const enviar = async () => {
    const texto = entrada.trim()
    if (!texto || cargando) return

    setEntrada('')
    setLineas(l => [...l, { de: 'yo', texto }])
    setCargando(true)

    try {
      // El primer mensaje siempre lo manda el cliente: el bot nunca escribe primero, y si
      // el simulador saludara solo estaría probando algo que no pasa.
      //
      // `leerTodo` es la diferencia entre los dos bots, y no es cosmética: el de soporte
      // se suma a una charla en curso y lo primero que ve es el reporte, así que ese texto
      // se lee contra el flujo entero antes de arrancar.
      const d = turno?.nodo
        ? await llamar({ nodo: turno.nodo, entrada: texto, datos: turno.datos, slots })
        : await llamar(leerTodo ? { leer_todo: true, entrada: texto } : {})
      pintar(d)

      // Primer turno: el saludo sale, y la respuesta que el cliente acaba de escribir
      // todavía no se procesó. Se encadena para no perderla.
      //
      // Si el encadenado se queda en el MISMO paso —porque lo que escribió no traía el
      // dato que se le pedía— el motor vuelve a emitir la misma pregunta, y acá ya se
      // pintó: se muestra una sola vez. En producción no se nota porque el saludo y la
      // repetición están separados por el mensaje del cliente; acá van juntos y se leía
      // como si el bot se repitiera.
      // Si el servidor ya leyó el mensaje al arrancar, encadenar lo procesaría dos veces.
      if (!turno?.nodo && d.turno?.nodo && !d.leyo_entrada) {
        const seg = await llamar({ nodo: d.turno.nodo, entrada: texto, datos: d.turno.datos, slots: d.slots ?? [] })
        const mismoPaso = seg.turno?.nodo === d.turno.nodo
        pintar(seg, { silenciarMensajes: mismoPaso })
      }
    } catch (e: any) {
      setLineas(l => [...l, { de: 'sistema', texto: e.message ?? 'Algo falló' }])
    } finally {
      setCargando(false)
    }
  }

  const pista = !turno ? 'Escribí como lo haría un desconocido…'
    : turno.estado === 'espera_opcion' ? 'Elegí una opción…'
    : turno.estado === 'espera_datos' ? 'Respondé lo que el bot preguntó…'
    : 'La conversación terminó — reiniciá para probar de nuevo'

  const terminado = turno?.estado === 'fin' || turno?.estado === 'handoff'

  return (
    <div className="chat-prueba">
      <div className="chat-prueba__cabecera">
        <span className="chat-prueba__avatar"><IconRobot size={15} /></span>
        <div className="chat-prueba__titulo">
          <strong>Chat de prueba</strong>
          <span>{borrador ? 'Corre el borrador guardado' : 'Corre lo publicado'} · no se envía nada</span>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={reiniciar} type="button" title="Empezar de nuevo">
          <IconRefresh size={13} />
        </button>
      </div>

      {haySinGuardar && (
        <div className="chat-prueba__aviso">
          Tenés cambios sin guardar. La prueba corre lo último <strong>guardado</strong>, no lo que está en pantalla.
        </div>
      )}

      <div className="chat-prueba__hilo">
        {lineas.length === 0 && (
          <p className="chat-prueba__vacio">
            Toda conversación empieza por el cliente. Escribí abajo como si fueras alguien
            que escribe por primera vez.
          </p>
        )}

        {lineas.map((l, i) => (
          <div key={i} className={`chat-prueba__linea chat-prueba__linea--${l.de}`}>
            {l.texto}
          </div>
        ))}

        {cargando && <div className="chat-prueba__linea chat-prueba__linea--sistema">…</div>}
        <div ref={fin} />
      </div>

      {turno?.opciones && turno.opciones.length > 0 && !terminado && (
        <div className="chat-prueba__opciones">
          {turno.opciones.map((o, i) => (
            <button key={i} className="chat-prueba__opcion" type="button" onClick={() => { setEntrada(String(i + 1)) }}>
              {i + 1}. {o}
            </button>
          ))}
        </div>
      )}

      <div className="chat-prueba__compositor">
        <input
          className="form-input"
          value={entrada}
          placeholder={pista}
          disabled={terminado || cargando}
          onChange={e => setEntrada(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void enviar() }}
        />
        <button className="btn btn-primary btn-sm" onClick={() => void enviar()} disabled={terminado || cargando} type="button">
          <IconSend size={14} />
        </button>
      </div>
    </div>
  )
}
